import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { clientAPI } from '../../services/api';
import ClientLayout from '../../components/ClientLayout';
import { 
  Shirt, 
  Sparkles, 
  PackageOpen, 
  Loader2,
  ArrowRight,
  Calendar,
  MessageSquare,
  Plus
} from 'lucide-react';

const ClientDashboard = () => {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  const navigate = useNavigate();
  const clientId = localStorage.getItem('clientId');

  useEffect(() => {
    if (!clientId) {
      navigate('/cabinet/login');
      return;
    }
    fetchDashboard();
  }, [clientId, navigate]);

  const fetchDashboard = async () => {
    try {
      const data = await clientAPI.getDashboard(clientId);
      setDashboard(data);
    } catch (err) {
      setError('Ошибка загрузки данных');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <ClientLayout>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '400px'
        }}>
          <Loader2 
            size={32} 
            style={{ color: 'var(--accent-gold)', animation: 'spin 1s linear infinite' }} 
          />
        </div>
        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </ClientLayout>
    );
  }

  if (error) {
    return (
      <ClientLayout>
        <div style={{ textAlign: 'center', padding: 'var(--space-xxl)' }}>
          <p style={{ color: '#ef4444' }}>{error}</p>
        </div>
      </ClientLayout>
    );
  }

  const { subscription, recent_looks, stats } = dashboard;

  return (
    <ClientLayout>
      <div style={{ maxWidth: '1000px' }} data-testid="client-dashboard">
        {/* Header */}
        <div style={{ marginBottom: 'var(--space-xxl)' }}>
          <h1 style={{
            fontFamily: 'var(--font-display)',
            fontSize: 'clamp(2rem, 4vw, 2.5rem)',
            fontWeight: 300,
            marginBottom: 'var(--space-s)'
          }}>
            Добро пожаловать
          </h1>
          <p style={{
            fontFamily: 'var(--font-body)',
            color: 'var(--mid-gray)',
            fontSize: '0.875rem'
          }}>
            Ваш персональный гардеробный консультант
          </p>
        </div>

        {/* Quick Action - New Request */}
        <Link
          to="/cabinet/requests"
          data-testid="new-request-cta"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: 'var(--black)',
            color: 'var(--white)',
            padding: 'var(--space-l) var(--space-xl)',
            marginBottom: 'var(--space-xl)',
            textDecoration: 'none',
            transition: 'all 200ms ease'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-m)' }}>
            <MessageSquare size={24} />
            <div>
              <p style={{
                fontFamily: 'var(--font-body)',
                fontSize: '1rem',
                fontWeight: 500,
                marginBottom: '2px'
              }}>
                Создать запрос
              </p>
              <p style={{
                fontFamily: 'var(--font-body)',
                fontSize: '0.8125rem',
                opacity: 0.8
              }}>
                Подбор образа или помощь с покупкой
              </p>
            </div>
          </div>
          <Plus size={20} />
        </Link>

        {/* Subscription Card */}
        <div style={{
          backgroundColor: 'var(--white)',
          padding: 'var(--space-xl)',
          marginBottom: 'var(--space-xl)',
          border: '1px solid var(--soft-gray)'
        }}
        data-testid="subscription-card"
        >
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            flexWrap: 'wrap',
            gap: 'var(--space-l)'
          }}>
            <div>
              <span style={{
                fontFamily: 'var(--font-body)',
                fontSize: '0.75rem',
                letterSpacing: '1px',
                textTransform: 'uppercase',
                color: 'var(--mid-gray)'
              }}>
                Ваша подписка
              </span>
              <h2 style={{
                fontFamily: 'var(--font-display)',
                fontSize: '1.75rem',
                fontWeight: 400,
                marginTop: 'var(--space-s)'
              }}>
                {subscription ? subscription.type : 'Нет активной подписки'}
              </h2>
            </div>
            {subscription && (
              <div style={{
                display: 'flex',
                alignItems: 'baseline',
                gap: 'var(--space-s)'
              }}>
                <span style={{
                  fontFamily: 'var(--font-display)',
                  fontSize: '3rem',
                  fontWeight: 300,
                  color: subscription.remaining > 0 ? 'var(--warm-sage)' : '#ef4444'
                }}
                data-testid="remaining-looks"
                >
                  {subscription.remaining}
                </span>
                <span style={{
                  fontFamily: 'var(--font-body)',
                  fontSize: '0.875rem',
                  color: 'var(--mid-gray)'
                }}>
                  {subscription.remaining === 1 ? 'подбор остался' : 'подборов осталось'}
                </span>
              </div>
            )}
          </div>
          
          {subscription && (
            <div style={{
              marginTop: 'var(--space-l)',
              paddingTop: 'var(--space-l)',
              borderTop: '1px solid var(--soft-gray)',
              display: 'flex',
              gap: 'var(--space-xl)',
              flexWrap: 'wrap'
            }}>
              <div>
                <span style={{ 
                  fontFamily: 'var(--font-body)', 
                  fontSize: '0.75rem', 
                  color: 'var(--light-gray)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  Всего подборов
                </span>
                <p style={{ 
                  fontFamily: 'var(--font-display)', 
                  fontSize: '1.25rem',
                  marginTop: '4px'
                }}>
                  {subscription.total}
                </p>
              </div>
              <div>
                <span style={{ 
                  fontFamily: 'var(--font-body)', 
                  fontSize: '0.75rem', 
                  color: 'var(--light-gray)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  Использовано
                </span>
                <p style={{ 
                  fontFamily: 'var(--font-display)', 
                  fontSize: '1.25rem',
                  marginTop: '4px'
                }}>
                  {subscription.used}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Stats Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: 'var(--space-m)',
          marginBottom: 'var(--space-xxl)'
        }}>
          <Link to="/cabinet/wardrobe" style={{ textDecoration: 'none' }}>
            <div style={{
              backgroundColor: 'var(--white)',
              padding: 'var(--space-xl)',
              border: '1px solid var(--soft-gray)',
              transition: 'all 300ms ease',
              cursor: 'pointer'
            }}
            data-testid="stat-wardrobe"
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.08)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
            >
              <Shirt size={24} style={{ color: 'var(--accent-gold)', marginBottom: 'var(--space-m)' }} />
              <p style={{
                fontFamily: 'var(--font-display)',
                fontSize: '2rem',
                fontWeight: 300,
                marginBottom: '4px'
              }}>
                {stats?.wardrobe_items || 0}
              </p>
              <span style={{
                fontFamily: 'var(--font-body)',
                fontSize: '0.75rem',
                color: 'var(--mid-gray)',
                textTransform: 'uppercase',
                letterSpacing: '1px'
              }}>
                Вещей
              </span>
            </div>
          </Link>

          <Link to="/cabinet/looks" style={{ textDecoration: 'none' }}>
            <div style={{
              backgroundColor: 'var(--white)',
              padding: 'var(--space-xl)',
              border: '1px solid var(--soft-gray)',
              transition: 'all 300ms ease',
              cursor: 'pointer'
            }}
            data-testid="stat-looks"
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.08)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
            >
              <Sparkles size={24} style={{ color: 'var(--accent-gold)', marginBottom: 'var(--space-m)' }} />
              <p style={{
                fontFamily: 'var(--font-display)',
                fontSize: '2rem',
                fontWeight: 300,
                marginBottom: '4px'
              }}>
                {stats?.total_looks || 0}
              </p>
              <span style={{
                fontFamily: 'var(--font-body)',
                fontSize: '0.75rem',
                color: 'var(--mid-gray)',
                textTransform: 'uppercase',
                letterSpacing: '1px'
              }}>
                Образов
              </span>
            </div>
          </Link>

          <Link to="/cabinet/capsules" style={{ textDecoration: 'none' }}>
            <div style={{
              backgroundColor: 'var(--white)',
              padding: 'var(--space-xl)',
              border: '1px solid var(--soft-gray)',
              transition: 'all 300ms ease',
              cursor: 'pointer'
            }}
            data-testid="stat-capsules"
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.08)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
            >
              <PackageOpen size={24} style={{ color: 'var(--accent-gold)', marginBottom: 'var(--space-m)' }} />
              <p style={{
                fontFamily: 'var(--font-display)',
                fontSize: '2rem',
                fontWeight: 300,
                marginBottom: '4px'
              }}>
                {stats?.capsules || 0}
              </p>
              <span style={{
                fontFamily: 'var(--font-body)',
                fontSize: '0.75rem',
                color: 'var(--mid-gray)',
                textTransform: 'uppercase',
                letterSpacing: '1px'
              }}>
                Капсул
              </span>
            </div>
          </Link>
        </div>

        {/* Recent Looks */}
        {recent_looks && recent_looks.length > 0 && (
          <div>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 'var(--space-l)'
            }}>
              <h3 style={{
                fontFamily: 'var(--font-display)',
                fontSize: '1.5rem',
                fontWeight: 300
              }}>
                Последние образы
              </h3>
              <Link 
                to="/cabinet/looks" 
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-s)',
                  fontFamily: 'var(--font-body)',
                  fontSize: '0.8125rem',
                  color: 'var(--mid-gray)',
                  textDecoration: 'none',
                  transition: 'color 200ms ease'
                }}
              >
                Все образы
                <ArrowRight size={16} />
              </Link>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
              gap: 'var(--space-m)'
            }}>
              {recent_looks.slice(0, 4).map((look) => (
                <div
                  key={look.id}
                  style={{
                    backgroundColor: 'var(--white)',
                    border: '1px solid var(--soft-gray)',
                    overflow: 'hidden'
                  }}
                >
                  {look.look_images?.[0] && (
                    <div style={{
                      height: '180px',
                      overflow: 'hidden'
                    }}>
                      <img
                        src={look.look_images[0]}
                        alt={look.name}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                    </div>
                  )}
                  <div style={{ padding: 'var(--space-m)' }}>
                    <h4 style={{
                      fontFamily: 'var(--font-display)',
                      fontSize: '1.125rem',
                      fontWeight: 400,
                      marginBottom: 'var(--space-s)'
                    }}>
                      {look.name}
                    </h4>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--space-s)',
                      fontSize: '0.75rem',
                      color: 'var(--light-gray)'
                    }}>
                      <Calendar size={12} />
                      {new Date(look.created_at).toLocaleDateString('ru-RU')}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </ClientLayout>
  );
};

export default ClientDashboard;
